import { ActionFunction, redirect } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// Actions
import { saveData, createBasicAction, seedBasicActionOffline } from './saveData';
import { isActuallyOnline } from '../../../../offline/checkConnectivity';

// Utils
import { compressSessionStorage } from '../../../../utils/sessionStorage';
import { newCreationDraftId } from './creationDraft';

// Constants
import { formActionNaming } from '../../../../components/forms/ActionForms/constants';
import { isOfflineQueuedError } from '../../../../offline/isOfflineQueuedError';
import {
  showBackendErrorToast,
  showSuccessToast,
} from '../../../../utils/toasts';
import i18n from '../../../../i18n';

export const ActionNewAction: ActionFunction = async ({ request, params }) => {
  const formData = await request.formData();
  let intent = formData.get('intent');

  // P9 auto-create (2026-07-21): en el RV, "Crear Actuació" y el cierre de
  // tramo con causa Actuació crean la básica CON datos heredados y aterrizan
  // directo en el wizard reducido — sin form de Descripción.
  //
  // 2026-09-02: sin red ya NO se degrada al formulario de la normal (que
  // producía una actuación sin `containerType`, sin vínculo al tramo y con
  // redirect al detalle de OT). Se siembra la misma semilla sin crear nada y
  // se entra al wizard; la fase 2 la encola y el replay la crea y la vincula.
  // Si crear con red falla, se vuelve al recorrido: el formulario de la normal
  // no sale nunca desde un RV. El toast de negocio lo emite la escalera de
  // Apollo, como hasta ahora.
  if (intent === 'create-basic-action') {
    // El bloque SR del router usa `:routeId` (el `:id` es el de work-orders).
    const routeId = params.routeId ?? params.id;
    if (routeId) {
      let fromSegmentId: string | null = null;
      try {
        fromSegmentId = new URL(request.url).searchParams.get('fromSegment');
      } catch {
        // sin query — botón de la card, sin tramo que vincular
      }
      const basicParams = {
        routeId,
        orderParentId: String(formData.get('id') ?? ''),
        orderParentRoid: String(formData.get('roid') ?? ''),
        fromSegmentId,
        description: formData.get('description')
          ? String(formData.get('description'))
          : null,
      };
      try {
        const online = await isActuallyOnline();
        return online
          ? await createBasicAction(basicParams)
          : await seedBasicActionOffline(basicParams);
      } catch (err) {
        Sentry.captureException(err);
        console.error('> NewAction create-basic-action error:', err);
        // QA T3.4+a / T3.5+a (15-oct): se volvía al recorrido SIN decir nada,
        // y quien había pulsado no sabía que la básica no se había creado. El
        // cliente Apollo no avisa de los errores por su cuenta: aquí sí, con
        // el texto del código del back (p. ej. «Conflicto con otra operación
        // simultánea… Vuelve a intentarlo») o el genérico.
        showBackendErrorToast(err, i18n.t('toasts:error.generic'));
        return redirect(`/surveillance-route/${routeId}/child-orders`);
      }
    }
    // Sin recorrido no hay básica: el flujo de la normal.
    intent = 'pass-data';
  }
  if (intent === 'pass-data') {
    try {
      // 🔴 SUSTITUYE el borrador anterior, no lo mezcla (auditoría de creación
      // 2.4). Antes, si `new-action-data` ya existía, solo se pisaba el padre y
      // se heredaban `orderId`/`actionId`, la marca sin red (`_isOffline`: la
      // fase 2 encolaría OTRA alta), la de básica (`containerType`,
      // `routeId`) y el `fromSegmentId`. Hoy la única puerta (ShowOT) limpia
      // antes, pero una puerta nueva que no lo hiciera abriría la actuación
      // equivocada. Empezar una creación = borrador nuevo con su `draftId`,
      // que es el dueño de las copias de las pestañas y la `key` de la
      // pantalla (spec 2026-11-02 wizard-draft-ownership § 6).
      const orderParentId = formData.get('id');
      const orderParentRoid = formData.get('roid');
      sessionStorage.setItem(
        formActionNaming.NEW_ACTION_DATA,
        compressSessionStorage(
          JSON.stringify({
            draftId: newCreationDraftId(),
            orderParentId,
            orderParentRoid,
          })
        )
      );
      return { orderParentId, orderParentRoid };
    } catch (err) {
      Sentry.captureException(err);
      console.log('> NewAction pass-data - error creating:', err);
    }
  }
  if (intent === 'save-data') {
    const orderParentId = formData.get('orderParentId');
    const orderParentRoid = formData.get('orderParentRoid');
    try {
      const dataFormRaw = sessionStorage.getItem(
        formActionNaming.NEW_ACTION_DATA
      );

      // C-11.9: `await` para que el catch pueda atrapar errores async — antes
      // `return saveData(...)` retornaba un promise sin esperar y la rejection
      // (incluido `OfflineQueueLink.offlineQueued`) saltaba al ErrorBoundary
      // del router → ErrorPage en lugar de redirect limpio.
      return await saveData(
        dataFormRaw,
        orderParentId,
        orderParentRoid,
        formActionNaming.NEW_ACTION_DATA
      );
    } catch (err) {
      // C-11.9: si SAVE_ACTION fue encolada por OfflineQueueLink (race entre el
      // gate `isActuallyOnline()` del componente y la mutation real), tratar
      // como éxito y volver al parent OT. La mutation se replay-eará al
      // reconectar (cola individual `rois_offline_mutation_queue`).
      if (isOfflineQueuedError(err)) {
        sessionStorage.removeItem(formActionNaming.NEW_ACTION_DATA);
        // Avisa, como los otros trece asistentes: encolar en silencio deja al
        // operario sin saber si su trabajo se guardó (auditoría 2026-09-12).
        showSuccessToast(i18n.t('modals:changeState.queuedOffline'));
        // Y respeta el destino de quien encoló, si lo trae.
        const destino = (err as { redirectTo?: string }).redirectTo;
        return redirect(destino ?? `/work-orders/${orderParentId ?? ''}`);
      }
      Sentry.captureException(err);
      console.error('> NewAction save-data error:', err);
    }
  }

  return null;
};
